import { useEffect, useMemo, useState } from 'react'

import type { AssetReportContextItem } from '@digital-twins/deck-ui/components/charts/asset-report-label'

import { useQuestionFigure, useSurveyFigures } from './SurveyFiguresProvider'
import { ALL_SAVED_SEGMENTS_KEY, buildFigureDimensions, defaultFigureDimensionValue } from './figureDimensions'

import {
  StudyFiguresPopulationSource,
  isUnservablePopulationFigureId,
  parsePopulationFigureId,
  StudyFigureFilter,
} from '@digital-twins/shared-types'
import { FigureEntry, FigureRequestShape } from './figureBatcher'
import { FigureDimension, FigureDimensionValue } from './figureDimensions'
import { SurveyFiguresMetaState } from './SurveyFiguresProvider'

/**
 * SurveyJS types the figure service draws a question figure for (the D1
 * producers: choice, ranking, rating, matrix, continuous). Free text and text
 * matrices keep their own path, except a categorical free text and a themed
 * open end, which the card names explicitly (Wave 7 S6, S7).
 */
const FIGURE_QUESTION_TYPES: Set<string> = new Set([
  'radiogroup',
  'dropdown',
  'boolean',
  'checkbox',
  'rating',
  'ranking',
  'matrix',
  'matrixdropdown',
  'matrixdynamic',
])

export function isFigureQuestionType(question: any): boolean {
  if (question?.type === 'text') {
    return question.inputType === 'number'
  }
  return FIGURE_QUESTION_TYPES.has(question?.type)
}

export interface UseQuestionCardFiguresInput {
  question: any
  /** The card draws a chart (not a themed open end, a text matrix or the lift view). */
  isEligible: boolean
  /** Today's rule: this card's chart can split by a segment at all. */
  canSegment: boolean
  segmentableQuestions: Array<any>
  canonicalQuestionMap?: Record<string, any> | null
  /** The compiled `assetContext` items, for the report's asset answer labels. */
  assetContextItems?: Array<AssetReportContextItem> | null
  /** Today's "Asset shown" dimension name. */
  assignedAssetLabel: string
  /** The combined saved-segment cut, in the UI locale. */
  allSubsegmentsLabel: string
  /** Lane-merged cards filter by one value of a dimension, then unfold (no cut). */
  isLaneMergedCard: boolean
  /** Opens on the assigned-asset cut, as today, when the card can split. */
  defaultsToAsset: boolean
  /**
   * The figure's id when it is not the canonical question's: a population
   * column (`population:<measure>:<column>`, Wave 7 S5).
   */
  figureQuestionId?: string | null
  /** A free-text question whose answers chart as categories (Wave 7 S6). */
  isCategoricalText?: boolean
  /**
   * A themed open end whose theme cards the service stamps (open-end themes
   * plan): it filters by one value of a dimension, like a lane-merged card,
   * and never cuts.
   */
  isThemed?: boolean
  /**
   * A themed card's theme ids (the words it holds): stamped theme cards none of
   * whose ids is among them are a figure the service did not draw for this card.
   */
  themeIds?: Array<string> | null
}

export interface QuestionCardFigures {
  /** True on the survey tab for a card drawn from the figure service. */
  isFigureMode: boolean
  /** The figure service's list of cuts: loading, ready or failed (drawn visibly). */
  metaStatus: SurveyFiguresMetaState['status'] | null
  retryMeta: () => void
  /** Where the Population group comes from; `none` is said in a note. */
  populationSource: StudyFiguresPopulationSource | null
  dimensions: Array<FigureDimension>
  activeDimension: FigureDimension | null
  selectDimension: (key: string | null) => void
  /** Lane-merged and themed cards: the dimension's values and the one filtered on. */
  valueOptions: Array<FigureDimensionValue>
  activeValueId: string | null
  selectValue: (valueId: string) => void
  /** The figure entry; cut groups carry the service's labels (UI locale for complements). */
  entry: FigureEntry
  /** True when the drawn panels are the cut's groups. */
  isCut: boolean
  retry: () => void
  /**
   * The service answered the card's default view without this added kind's
   * figure (Wave 7: not categorical by Python's rule, no column value, a theme
   * package it cannot join). The card leaves figure mode; Python's answer is
   * final, so a categorical free text then lists its answers as text.
   */
  isDeclinedByService: boolean
  /**
   * The service answered a view the reader picked (a cut or a filter) without
   * the figure: the card stays in figure mode, keeps its controls and says the
   * figure is not charted for this group.
   */
  isNotChartedForView: boolean
}

function mergeFigureFilters(
  ...groups: Array<Array<StudyFigureFilter> | undefined>
): Array<StudyFigureFilter> | undefined {
  const bySource = new Map<string, StudyFigureFilter>()
  for (const group of groups) {
    for (const filter of group ?? []) {
      bySource.set(`${filter.kind}:${filter.source}`, filter)
    }
  }
  const merged = Array.from(bySource.values())
  return merged.length > 0 ? merged : undefined
}

/**
 * One QuestionCard's figure request on the survey tab (plots plan D3): the
 * cuts the figure service listed for this study (never guessed from the
 * survey), the chosen one, and the figure entry batched with every other card.
 * Inert (no request, no dimensions) outside the survey tab's figure source.
 */
export function useQuestionCardFigures(input: UseQuestionCardFiguresInput): QuestionCardFigures {
  const {
    question,
    isEligible,
    canSegment,
    segmentableQuestions,
    canonicalQuestionMap,
    assetContextItems = null,
    assignedAssetLabel,
    allSubsegmentsLabel,
    isLaneMergedCard,
    defaultsToAsset,
    figureQuestionId = null,
    isCategoricalText = false,
    isThemed = false,
    themeIds = null,
  } = input

  const source = useSurveyFigures()
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [hasUserSelected, setHasUserSelected] = useState(false)
  const [selectedValueId, setSelectedValueId] = useState<string | null>(null)

  const questionId: string | null =
    figureQuestionId ?? canonicalQuestionMap?.[question?.name]?.questionId ?? question?._canonical?.questionId ?? null
  // The kinds Wave 7 moved onto the service: a figure the service does not draw
  // (not categorical by its rule, no column value) keeps the card's own chart.
  const isAddedKind = figureQuestionId != null || isCategoricalText || isThemed
  const ownPopulationColumn = questionId ? parsePopulationFigureId(questionId)?.column ?? null : null
  const isFigureCandidate =
    source != null &&
    source.meta.status !== 'refused' &&
    isEligible &&
    questionId != null &&
    // A population figure id the route cannot take (too long, malformed) is
    // never asked for: that card keeps its own chart.
    !isUnservablePopulationFigureId(questionId) &&
    (isFigureQuestionType(question) || isCategoricalText || isThemed)
  const meta = source?.meta.status === 'ready' ? source.meta.meta : null
  // Today's rule: a chart that cannot split offers no dropdown; a lane-merged
  // card filters instead of splitting, so it keeps its dropdown.
  const offersSegments = isFigureCandidate && (canSegment || isLaneMergedCard)

  const dimensions = useMemo(() => {
    if (!offersSegments || !meta) {
      return []
    }
    const all = buildFigureDimensions({
      meta,
      questionName: question.name,
      surveyQuestions: segmentableQuestions,
      canonicalQuestionMap,
      assetContextItems,
      labels: { assignedAsset: assignedAssetLabel, allSubsegments: allSubsegmentsLabel },
    })
    // A lane-merged card already splits by asset; a themed open end never
    // offers it (its cards print one figure per version); a population column
    // never splits by itself. Those cards filter to one value, so the
    // side-by-side saved-segment break is not a choice for them.
    const isFilterCard = isLaneMergedCard || isThemed
    return all.filter(
      (dimension) =>
        !(isFilterCard && dimension.key === ALL_SAVED_SEGMENTS_KEY) &&
        !((isLaneMergedCard || isThemed) && dimension.group === 'asset') &&
        !(ownPopulationColumn != null && dimension.key === `population:${ownPopulationColumn}`),
    )
  }, [
    offersSegments,
    meta,
    question,
    segmentableQuestions,
    canonicalQuestionMap,
    assetContextItems,
    assignedAssetLabel,
    allSubsegmentsLabel,
    isLaneMergedCard,
    isThemed,
    ownPopulationColumn,
  ])

  const activeDimension = useMemo(() => {
    if (hasUserSelected) {
      return dimensions.find((dimension) => dimension.key === selectedKey) ?? null
    }
    if (defaultsToAsset) {
      return dimensions.find((dimension) => dimension.group === 'asset') ?? null
    }
    return null
  }, [dimensions, hasUserSelected, selectedKey, defaultsToAsset])

  // A newly chosen dimension starts back at its own default value.
  useEffect(() => {
    setSelectedValueId(null)
  }, [activeDimension?.key])

  // A lane-merged card unfolds by asset and a themed card prints one figure
  // per version: both filter by one value of the dimension, never cut.
  const isFilterCard = isLaneMergedCard || isThemed
  const valueOptions = useMemo(
    () => (isFilterCard && activeDimension ? activeDimension.values.filter((value) => value.n > 0) : []),
    [isFilterCard, activeDimension],
  )
  const activeValue =
    valueOptions.find((value) => value.id === selectedValueId) ??
    (activeDimension && isFilterCard ? defaultFigureDimensionValue(activeDimension) : null)

  const shape = useMemo<FigureRequestShape>(() => {
    const shared = source?.sharedFilters
    if (!activeDimension) {
      const filters = mergeFigureFilters(shared)
      return filters ? { filters } : {}
    }
    const local: FigureRequestShape = isFilterCard
      ? activeValue
        ? { filters: [activeValue.filter] }
        : {}
      : { cut: activeDimension.cut }
    const filters = mergeFigureFilters(shared, local.filters)
    return { ...local, ...(filters ? { filters } : {}) }
  }, [activeDimension, activeValue, isFilterCard, source?.sharedFilters])

  useEffect(() => {
    if (!isFilterCard || !activeValue || !source?.upsertSharedFilter) {
      return
    }
    source.upsertSharedFilter(activeValue.filter)
  }, [isFilterCard, activeValue, source])

  const entry = useQuestionFigure(source, isFigureCandidate ? questionId : null, shape)
  // The service refusing the whole sample (Total, no filter) refuses the study:
  // the card keeps today's chart. A refused cut stays a visible figure state.
  // A refusal of the card's default figure (Total, or the asset cut it opens on;
  // never a cut the reader picked) is a refusal of the study: today's chart.
  // Under the `legacy` policy (summary, data mode) any failure of the default
  // figure, or of the list of cuts, falls back the same way.
  const isDefaultShape = !hasUserSelected && selectedValueId == null
  const fallsBackOnAnyFailure = source?.failurePolicy === 'legacy'
  const isDefaultFailed =
    isDefaultShape &&
    entry.status === 'error' &&
    (entry.failure === 'rejected' || fallsBackOnAnyFailure)
  const isMetaFailedUnderLegacy = fallsBackOnAnyFailure && source?.meta.status === 'failed'
  // The service declining an added kind's figure (not categorical by its
  // rule, no value, a package it cannot join): on the default view the card
  // leaves figure mode; on a view the reader picked it stays, with its
  // controls, and says "not charted for this group".
  // A themed card reads only the stamped theme cards: an entry without them
  // (a python-api older than this client), or whose themes match none of the
  // card's words (a package re-run since), is a figure the service did not draw.
  const themeCards = entry.status === 'ready' && entry.question?.kind === 'theme-cards' ? entry.question.themeCards : null
  const isEntryDrawn =
    entry.status === 'ready' &&
    entry.question != null &&
    (!isThemed ||
      (themeCards != null &&
        (themeIds == null || themeCards.themes.some((card) => themeIds.includes(card.themeId)))))
  // A filtered Total under the figure floor is still this card's figure. The view
  // says the group is too small, instead of the legacy chart or "not charted".
  const isTooFewFilteredTotal =
    shape.cut == null &&
    entry.status === 'ready' &&
    entry.groups.some((group) => group.kind === 'total' && !group.drawn && group.reason === 'too_few')
  const isAddedKindUndrawn =
    isFigureCandidate && isAddedKind && entry.status === 'ready' && !isEntryDrawn && !isTooFewFilteredTotal
  const isDeclinedByService = isAddedKindUndrawn && isDefaultShape
  const isNotChartedForView = isAddedKindUndrawn && !isDefaultShape
  const isFigureMode = isFigureCandidate && !isDefaultFailed && !isMetaFailedUnderLegacy && !isDeclinedByService

  return {
    isFigureMode,
    metaStatus: offersSegments && source ? source.meta.status : null,
    retryMeta: () => source?.retryMeta(),
    populationSource: offersSegments && meta ? meta.populationSource : null,
    dimensions,
    activeDimension,
    selectDimension: (key) => {
      setHasUserSelected(true)
      setSelectedKey(key)
    },
    valueOptions,
    activeValueId: activeValue?.id ?? null,
    selectValue: setSelectedValueId,
    entry,
    isCut: shape.cut != null,
    retry: () => source?.batcher.retry(shape),
    isDeclinedByService,
    isNotChartedForView: isFigureMode && isNotChartedForView,
  }
}
